import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle, Check, CheckCircle, ChevronDown, Copy, X } from 'lucide-react';

interface SyncDetailsModalProps {
	isOpen: boolean;
	onClose: () => void;
	activity: any;
}

export const SyncDetailsModal: React.FC<SyncDetailsModalProps> = ({ isOpen, onClose, activity }) => {
	const [copied, setCopied] = useState(false);
	const [copiedSource, setCopiedSource] = useState(false);
	const [copiedTarget, setCopiedTarget] = useState(false);
	const [copiedHttp, setCopiedHttp] = useState(false);
	const [showSourceData, setShowSourceData] = useState(false);
	const [showTargetData, setShowTargetData] = useState(false);
	const [showHttpRequest, setShowHttpRequest] = useState(false);

	if (!isOpen || !activity) return null;

	// Determine failure type
	const isFailed = activity.status === 'FAILED';
	const isManuallyTriggered = activity.status === 'MANUALLY_TRIGGERED';
	const isPartialFailure =
		(isFailed || isManuallyTriggered) && activity.partialResults && Object.keys(activity.partialResults).length > 0;

	const isTotalFailure = (isFailed || isManuallyTriggered) && !isPartialFailure;

	const handleCopyError = async () => {
		const errorInfo = {
			syncLogId: activity.id,
			entityType: activity.entityType,
			entityId: activity.entityId,
			action: activity.action,
			status: activity.status,
			failureType: isPartialFailure ? 'PARTIAL' : 'TOTAL',
			error: activity.error,
			errorType: activity.errorType,
			partialResults: activity.partialResults,
			attemptCount: activity.attemptCount,
			timestamp: activity.timestamp,
		};

		try {
			await navigator.clipboard.writeText(JSON.stringify(errorInfo, null, 2));
			setCopied(true);
			setTimeout(() => setCopied(false), 2000);
		} catch (err) {
			console.error('Copy failed:', err);
			// Fallback: Create downloadable text file
			const blob = new Blob([JSON.stringify(errorInfo, null, 2)], {
				type: 'text/plain',
			});
			const url = URL.createObjectURL(blob);
			const a = document.createElement('a');
			a.href = url;
			a.download = `sync-error-${activity.id}.json`;
			a.click();
			URL.revokeObjectURL(url);
		}
	};

	const handleCopySourceData = async () => {
		try {
			await navigator.clipboard.writeText(JSON.stringify(activity.sourceData, null, 2));
			setCopiedSource(true);
			setTimeout(() => setCopiedSource(false), 2000);
		} catch (err) {
			console.error('Copy failed:', err);
		}
	};

	const handleCopyTargetData = async () => {
		try {
			await navigator.clipboard.writeText(JSON.stringify(activity.targetData, null, 2));
			setCopiedTarget(true);
			setTimeout(() => setCopiedTarget(false), 2000);
		} catch (err) {
			console.error('Copy failed:', err);
		}
	};

	const handleCopyHttpRequest = async () => {
		try {
			await navigator.clipboard.writeText(JSON.stringify(activity.httpRequest, null, 2));
			setCopiedHttp(true);
			setTimeout(() => setCopiedHttp(false), 2000);
		} catch (err) {
			console.error('Copy failed:', err);
		}
	};

	return createPortal(
		<div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[55] p-4">
			<div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
				<div className="flex items-center justify-between p-6 border-b">
					<div>
						<h2 className="text-xl font-semibold text-gray-900">Sync Details</h2>
						<p className="text-sm text-gray-500 mt-1">
							{activity.clientName} - {activity.action}
						</p>
					</div>
					<button onClick={onClose} className="text-gray-400 hover:text-gray-600" type="button">
						<X className="h-6 w-6" />
					</button>
				</div>

				<div className="p-6 space-y-6">
					{/* Status and Basic Info */}
					<div className="grid grid-cols-1 md:grid-cols-3 gap-6">
						<div>
							<label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
							<span
								className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${
									activity.status === 'COMPLETED'
										? 'bg-green-100 text-green-800'
										: activity.status === 'FAILED'
											? 'bg-red-100 text-red-800'
											: activity.status === 'MANUALLY_TRIGGERED'
												? 'bg-orange-100 text-orange-800'
												: 'bg-yellow-100 text-yellow-800'
								}`}>
								{activity.status}
							</span>
						</div>
						<div>
							<label className="block text-sm font-medium text-gray-700 mb-1">Processing Time</label>
							<p className="text-sm text-gray-900">
								{activity.processingTime ? `${activity.processingTime}ms` : 'N/A'}
							</p>
						</div>
						<div>
							<label className="block text-sm font-medium text-gray-700 mb-1">Attempts</label>
							<p className="text-sm text-gray-900">{activity.attemptCount || 0}</p>
						</div>
					</div>

					{/* Failure Type (if failed) */}
					{(isFailed || isManuallyTriggered) && (
						<div
							className={`border rounded-lg p-4 ${isFailed ? 'bg-red-50 border-red-200' : 'bg-orange-50 border-orange-200'}`}>
							<div className="flex items-start space-x-3">
								<AlertTriangle className={`h-5 w-5 mt-0.5 ${isFailed ? 'text-red-600' : 'text-orange-600'}`} />
								<div className="flex-1">
									<h4 className={`text-sm font-semibold mb-2 ${isFailed ? 'text-red-900' : 'text-orange-900'}`}>
										{isManuallyTriggered
											? isPartialFailure
												? 'Partial Failure (Manually Triggered)'
												: 'Failed (Manually Triggered)'
											: isPartialFailure
												? 'Partial Failure'
												: 'Failed'}
									</h4>

									{isPartialFailure && (
										<div className="space-y-2">
											<p className="text-sm text-red-800">Some steps completed successfully before failure occurred.</p>

											{/* Show checkpoint status dynamically */}
											<div className="mt-3 space-y-1">
												{Object.entries(activity.partialResults || {})
													.filter(([key]) => key !== 'msdError' && key !== 'odooError')
													.map(([key, value]) => {
														// Skip non-boolean checkpoint flags
														if (typeof value !== 'boolean' && value !== true && value !== false) {
															return null;
														}

														// Convert camelCase to readable format
														const readableKey = key
															.replace(/([A-Z])/g, ' $1')
															.replace(/^./, (str) => str.toUpperCase())
															.trim();

														return (
															<div key={key} className="flex items-center space-x-2">
																{value ? (
																	<CheckCircle className="h-4 w-4 text-green-600" />
																) : (
																	<AlertTriangle className="h-4 w-4 text-red-600" />
																)}
																<span className="text-sm text-gray-700">
																	{readableKey}
																	{value && <span className="text-green-600 ml-2">✓ Completed</span>}
																</span>
															</div>
														);
													})}
											</div>

											{/* Show non-checkpoint data separately */}
											{Object.entries(activity.partialResults || {}).filter(
												([key, value]) =>
													key !== 'msdError' &&
													key !== 'odooError' &&
													typeof value !== 'boolean' &&
													value !== true &&
													value !== false,
											).length > 0 && (
												<div className="mt-3 bg-gray-50 border border-gray-200 rounded-lg p-3">
													<p className="text-xs font-medium text-gray-700 mb-2">Meta Data:</p>
													<div className="space-y-1">
														{Object.entries(activity.partialResults || {})
															.filter(
																([key, value]) =>
																	key !== 'msdError' &&
																	key !== 'odooError' &&
																	typeof value !== 'boolean' &&
																	value !== true &&
																	value !== false,
															)
															.map(([key, value]) => (
																<div key={key} className="text-xs text-gray-600">
																	<span className="font-medium">{key}:</span>{' '}
																	<span className="font-mono">{String(value)}</span>
																</div>
															))}
													</div>
												</div>
											)}

											{/* Show MSD Error if present */}
											{activity.partialResults?.msdError && (
												<div className="mt-3 bg-red-50 border border-red-200 rounded-lg p-3">
													<p className="text-xs font-medium text-red-700 mb-2">MSD Error Details:</p>
													<div className="space-y-1">
														{activity.partialResults.msdError.message && (
															<p className="text-xs text-red-600">
																<span className="font-medium">Message:</span> {activity.partialResults.msdError.message}
															</p>
														)}
														{activity.partialResults.msdError.apiAction && (
															<p className="text-xs text-red-600">
																<span className="font-medium">API Action:</span>{' '}
																{activity.partialResults.msdError.apiAction}
															</p>
														)}
														{activity.partialResults.msdError.exceptionType && (
															<p className="text-xs text-red-600">
																<span className="font-medium">Exception Type:</span>{' '}
																{activity.partialResults.msdError.exceptionType}
															</p>
														)}
														{activity.partialResults.msdError.activityId && (
															<p className="text-xs text-red-600">
																<span className="font-medium">Activity ID:</span>{' '}
																{activity.partialResults.msdError.activityId}
															</p>
														)}
													</div>
												</div>
											)}

											{/* Show Odoo Error if present — sibling, not child of msdError */}
											{activity.partialResults?.odooError && (
												<div className="mt-3 bg-red-50 border border-red-200 rounded-lg p-3">
													<p className="text-xs font-medium text-red-700 mb-2">Odoo Error Details:</p>
													<div className="space-y-1">
														{activity.partialResults.odooError.message && (
															<p className="text-xs text-red-600">
																<span className="font-medium">Message:</span>{' '}
																{activity.partialResults.odooError.message}
															</p>
														)}
														{activity.partialResults.odooError.type && (
															<p className="text-xs text-red-600">
																<span className="font-medium">Error Type:</span>{' '}
																{activity.partialResults.odooError.type}
															</p>
														)}
														{activity.partialResults.odooError.faultCode && (
															<p className="text-xs text-red-600">
																<span className="font-medium">Fault Code:</span>{' '}
																{activity.partialResults.odooError.faultCode}
															</p>
														)}
													</div>
												</div>
											)}
										</div>
									)}

									{isTotalFailure && <p className="text-sm text-red-800">Sync failed before completing any steps.</p>}
								</div>
							</div>
						</div>
					)}

					{/* Entity Details */}
					<div className="bg-gray-50 rounded-lg p-4">
						<h4 className="text-sm font-medium text-gray-900 mb-3">Entity Information</h4>
						<div className="grid grid-cols-1 md:grid-cols-3 gap-4">
							<div>
								<p className="text-xs text-gray-600">Entity Type</p>
								<p className="text-sm font-medium text-gray-900">{activity.entityType}</p>
							</div>
							<div>
								<p className="text-xs text-gray-600">Entity ID</p>
								<p className="text-sm font-mono bg-white px-2 py-1 rounded border text-gray-900">
									{activity.entityId || 'N/A'}
								</p>
							</div>
							<div>
								<p className="text-xs text-gray-600">Operation</p>
								<p className="text-sm font-medium text-gray-900">{activity.action?.split(' ').pop() || 'Unknown'}</p>
							</div>
						</div>
					</div>

					{/* Error Message (if failed) */}
					{(isFailed || isManuallyTriggered) && activity.error && (
						<div>
							<div className="flex items-center justify-between mb-2">
								<label className="block text-sm font-medium text-gray-700">Error Message</label>
								<button
									onClick={handleCopyError}
									className="inline-flex items-center px-3 py-1 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
									type="button">
									{copied ? (
										<>
											<Check className="h-3 w-3 mr-1 text-green-600" />
											Copied!
										</>
									) : (
										<>
											<Copy className="h-3 w-3 mr-1" />
											Copy Error
										</>
									)}
								</button>
							</div>
							<div className="bg-red-50 border border-red-200 rounded-lg p-4">
								<p className="text-sm text-red-800 font-mono whitespace-pre-wrap">{activity.error}</p>
								{activity.errorType && <p className="text-xs text-red-600 mt-2">Error Type: {activity.errorType}</p>}
							</div>
						</div>
					)}

					{/* Success Message (if completed) */}
					{activity.status === 'COMPLETED' && (
						<div className="bg-green-50 border border-green-200 rounded-lg p-4">
							<div className="flex items-center space-x-2">
								<CheckCircle className="h-5 w-5 text-green-600" />
								<p className="text-sm text-green-800 font-medium">Sync completed successfully</p>
							</div>
						</div>
					)}

					{/* Source Data */}
					{activity.sourceData && (
						<div className="border border-gray-200 rounded-lg p-3 bg-white">
							<div className="flex items-center justify-between mb-2">
								<button
									onClick={() => setShowSourceData(!showSourceData)}
									className="flex items-center text-left text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors">
									<span>Source Data</span>
									<ChevronDown className={`h-4 w-4 ml-2 transition-transform ${showSourceData ? 'rotate-180' : ''}`} />
								</button>
								<button
									onClick={handleCopySourceData}
									className="inline-flex items-center px-3 py-1 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
									type="button">
									{copiedSource ? (
										<>
											<Check className="h-3 w-3 mr-1 text-green-600" />
											Copied!
										</>
									) : (
										<>
											<Copy className="h-3 w-3 mr-1" />
											Copy
										</>
									)}
								</button>
							</div>
							{showSourceData && (
								<div className="mt-3 bg-gray-50 border border-gray-200 rounded-lg p-4 max-h-96 overflow-auto">
									<pre className="text-xs text-gray-800 whitespace-pre-wrap break-words">
										{JSON.stringify(activity.sourceData, null, 2)}
									</pre>
								</div>
							)}
						</div>
					)}

					{/* Target Data */}
					{activity.targetData && (
						<div className="border border-gray-200 rounded-lg p-3 bg-white">
							<div className="flex items-center justify-between mb-2">
								<button
									onClick={() => setShowTargetData(!showTargetData)}
									className="flex items-center text-left text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors">
									<span>Target Data</span>
									<ChevronDown className={`h-4 w-4 ml-2 transition-transform ${showTargetData ? 'rotate-180' : ''}`} />
								</button>
								<button
									onClick={handleCopyTargetData}
									className="inline-flex items-center px-3 py-1 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
									type="button">
									{copiedTarget ? (
										<>
											<Check className="h-3 w-3 mr-1 text-green-600" />
											Copied!
										</>
									) : (
										<>
											<Copy className="h-3 w-3 mr-1" />
											Copy
										</>
									)}
								</button>
							</div>
							{showTargetData && (
								<div className="mt-3 bg-gray-50 border border-gray-200 rounded-lg p-4 max-h-96 overflow-auto">
									<pre className="text-xs text-gray-800 whitespace-pre-wrap break-words">
										{JSON.stringify(activity.targetData, null, 2)}
									</pre>
								</div>
							)}
						</div>
					)}

					{/* HTTP Request - Only show when failed */}
					{(isFailed || isManuallyTriggered) && activity.httpRequest && (
						<div
							className={`border rounded-lg p-3 ${isFailed ? 'border-red-200 bg-red-50' : 'border-orange-200 bg-orange-50'}`}>
							<div className="flex items-center justify-between mb-2">
								<button
									onClick={() => setShowHttpRequest(!showHttpRequest)}
									className={`flex items-center text-left text-sm font-medium transition-colors ${isFailed ? 'text-red-700 hover:text-red-900' : 'text-orange-700 hover:text-orange-900'}`}>
									<span>HTTP Request ({isFailed ? 'Failed Payload' : 'Original Payload'})</span>
									<ChevronDown className={`h-4 w-4 ml-2 transition-transform ${showHttpRequest ? 'rotate-180' : ''}`} />
								</button>
								<button
									onClick={handleCopyHttpRequest}
									className={`inline-flex items-center px-3 py-1 text-xs font-medium bg-white border rounded-md ${isFailed ? 'text-red-700 border-red-300 hover:bg-red-50' : 'text-orange-700 border-orange-300 hover:bg-orange-50'}`}
									type="button">
									{copiedHttp ? (
										<>
											<Check className="h-3 w-3 mr-1 text-green-600" />
											Copied!
										</>
									) : (
										<>
											<Copy className="h-3 w-3 mr-1" />
											Copy
										</>
									)}
								</button>
							</div>
							{showHttpRequest && (
								<div
									className={`mt-3 bg-white border rounded-lg p-4 max-h-96 overflow-auto ${isFailed ? 'border-red-200' : 'border-orange-200'}`}>
									<pre className="text-xs text-gray-800 whitespace-pre-wrap break-words">
										{JSON.stringify(activity.httpRequest, null, 2)}
									</pre>
								</div>
							)}
						</div>
					)}
				</div>

				<div className="flex justify-end p-6 border-t">
					<button
						onClick={onClose}
						className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-lg hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500"
						type="button">
						Close
					</button>
				</div>
			</div>
		</div>,
		document.body,
	);
};
